import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table';
import type { DataTableParams } from './types';

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

export interface UseDataTableOptions {
  defaultLimit?: (typeof PAGE_SIZE_OPTIONS)[number];
  defaultSortBy?: string;
  defaultSortOrder?: 'asc' | 'desc';
}

export interface UseDataTableReturn {
  page: number;
  limit: number;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  /** Immediate (un-debounced) value — bind this to the search <input>. */
  searchInput: string;
  setSearchInput: (value: string) => void;
  /** Debounced params object, ready to spread into a list API call. */
  params: DataTableParams;
  pageSizeOptions: typeof PAGE_SIZE_OPTIONS;
}

/**
 * Owns page/limit/search/sorting for a server-driven list and syncs all of it
 * to the URL query string (?page=&limit=&search=&sortBy=&sortOrder=) so
 * refresh and the back button restore the same view.
 */
export function useDataTable(options: UseDataTableOptions = {}): UseDataTableReturn {
  const { defaultLimit = 20, defaultSortBy, defaultSortOrder } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get('page')) || 1);

  const limit = useMemo(() => {
    const raw = Number(searchParams.get('limit'));
    return (PAGE_SIZE_OPTIONS as readonly number[]).includes(raw)
      ? raw
      : defaultLimit;
  }, [searchParams, defaultLimit]);

  const urlSearch = searchParams.get('search') ?? '';
  const sortBy = searchParams.get('sortBy') ?? defaultSortBy;
  const sortOrder =
    (searchParams.get('sortOrder') as 'asc' | 'desc' | null) ?? defaultSortOrder;

  // Decoupled from the URL so typing stays snappy; debounced 300ms into the
  // URL param, which is what actually drives the API call.
  const [searchInput, setSearchInputState] = useState(urlSearch);

  useEffect(() => {
    if (searchInput === urlSearch) return;
    const t = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (searchInput) next.set('search', searchInput);
          else next.delete('search');
          next.set('page', '1'); // changing search resets page to 1
          return next;
        },
        { replace: true },
      );
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Keep the input in sync if the URL changes from elsewhere (back button).
  useEffect(() => {
    setSearchInputState(urlSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearch]);

  const setSearchInput = useCallback((value: string) => {
    setSearchInputState(value);
  }, []);

  const pagination: PaginationState = useMemo(
    () => ({ pageIndex: page - 1, pageSize: limit }),
    [page, limit],
  );

  const onPaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updater) => {
      setSearchParams(
        (prev) => {
          const current: PaginationState = {
            pageIndex: Math.max(1, Number(prev.get('page')) || 1) - 1,
            pageSize: limit,
          };
          const next =
            typeof updater === 'function' ? updater(current) : updater;
          const params = new URLSearchParams(prev);
          const pageSizeChanged = next.pageSize !== current.pageSize;
          params.set('limit', String(next.pageSize));
          // changing page size resets page to 1; otherwise navigate normally
          params.set('page', String(pageSizeChanged ? 1 : next.pageIndex + 1));
          return params;
        },
        { replace: true },
      );
    },
    [limit, setSearchParams],
  );

  const sorting: SortingState = useMemo(
    () => (sortBy ? [{ id: sortBy, desc: sortOrder === 'desc' }] : []),
    [sortBy, sortOrder],
  );

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      setSearchParams(
        (prev) => {
          const current: SortingState = sorting;
          const next =
            typeof updater === 'function' ? updater(current) : updater;
          const params = new URLSearchParams(prev);
          if (next.length === 0) {
            params.delete('sortBy');
            params.delete('sortOrder');
          } else {
            params.set('sortBy', next[0].id);
            params.set('sortOrder', next[0].desc ? 'desc' : 'asc');
          }
          return params;
        },
        { replace: true },
      );
    },
    [sorting, setSearchParams],
  );

  const params: DataTableParams = useMemo(
    () => ({
      page,
      limit,
      search: urlSearch || undefined,
      sortBy: sortBy || undefined,
      sortOrder: sortBy ? (sortOrder ?? 'asc') : undefined,
    }),
    [page, limit, urlSearch, sortBy, sortOrder],
  );

  return {
    page,
    limit,
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
    searchInput,
    setSearchInput,
    params,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
  };
}
